import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/Header';
import BalanceSheet from '../../components/FinancialStatements/BalanceSheet';
import ProfitLoss from '../../components/FinancialStatements/ProfitLoss';
import FinancialChart from '../../components/Charts/FinancialChart';
import { generateMockFinancialData, getFinancialSummary, ENTITY_SCENARIOS } from '../../services/mockFinancialData';
import './PersonalDashboard.css';

function PersonalDashboard() {
  const navigate = useNavigate();
  useEffect(() => { document.title = 'PEC - Finanzas'; }, []);
  
  const [activeView, setActiveView] = useState('overview'); // 'overview', 'balance-sheet', 'profit-loss'
  const [selectedMetric, setSelectedMetric] = useState('equity'); // Track which financial box is selected

  // Generate mock financial data for personal entity
  const financialData = useMemo(() => {
    return generateMockFinancialData('personal-entity', ENTITY_SCENARIOS.personal);
  }, []);

  // Get financial summary
  const summary = useMemo(() => {
    return getFinancialSummary('personal-entity', financialData);
  }, [financialData]);

  // Financial health indicators
  const getHealthScore = () => {
    const { rawRatios } = summary;
    let score = 0;
    
    // Current ratio (ideal: 1.5-3.0)
    if (rawRatios.currentRatio >= 1.5 && rawRatios.currentRatio <= 3.0) score += 25;
    else if (rawRatios.currentRatio >= 1.0) score += 15;
    
    // Net income positive
    if (rawRatios.netIncome > 0) score += 30;
    
    // Return on assets (ideal: > 5%)
    if (rawRatios.returnOnAssets > 0.05) score += 25;
    else if (rawRatios.returnOnAssets > 0.02) score += 15;
    
    // Asset to liability ratio
    const assetLiabilityRatio = rawRatios.totalLiabilities > 0 ? 
      rawRatios.totalAssets / rawRatios.totalLiabilities : 5;
    if (assetLiabilityRatio >= 2) score += 20;
    else if (assetLiabilityRatio >= 1.5) score += 10;
    
    return Math.min(score, 100);
  };

  const healthScore = getHealthScore();
  const getHealthColor = (score) => {
    if (score >= 80) return '#10b981'; // Green
    if (score >= 60) return '#f59e0b'; // Yellow
    return '#ef4444'; // Red
  };

  const getHealthStatus = (score) => {
    if (score >= 80) return 'Excellent';
    if (score >= 60) return 'Good';
    if (score >= 40) return 'Fair';
    return 'Needs Attention';
  };

  // Handle metric selection
  const handleMetricClick = (metric) => {
    setSelectedMetric(metric);
  };

  return (
    <div className="finance-page">
      <Header currentPage="finance" />
      <div className="finance-header" style={{ display: 'none' }}></div>

      <div className="finance-content">
        {activeView === 'overview' && (
          <div className="overview-section">
            {/* Financial Health Score */}
            <div className="health-score-card">
              <div className="health-score-header">
                <h3>Financial Health Score</h3>
                <div className="health-score-value" style={{ color: getHealthColor(healthScore) }}>
                  {healthScore}/100
                </div>
              </div>
              <div className="health-score-bar">
                <div 
                  className="health-score-progress" 
                  style={{ 
                    width: `${healthScore}%`,
                    backgroundColor: getHealthColor(healthScore)
                  }}
                ></div>
              </div>
              <div className="health-score-status">
                Status: <span style={{ color: getHealthColor(healthScore) }}>
                  {getHealthStatus(healthScore)}
                </span>
              </div>
            </div>

            {/* Key Financial Metrics - Single Row */}
            <div className="financial-summary-row">
              <div 
                className={`summary-card assets ${selectedMetric === 'assets' ? 'selected' : ''}`}
                onClick={() => handleMetricClick('assets')}
              >
                <div className="card-header">
                  <span className="card-icon">🏦</span>
                  <h3>Total Assets</h3>
                </div>
                <div className="card-value">{summary.totalAssets}</div>
                <div className="card-change positive">↗ Strong position</div>
              </div>

              <div 
                className={`summary-card liabilities ${selectedMetric === 'liabilities' ? 'selected' : ''}`}
                onClick={() => handleMetricClick('liabilities')}
              >
                <div className="card-header">
                  <span className="card-icon">💳</span>
                  <h3>Total Liabilities</h3>
                </div>
                <div className="card-value">{summary.totalLiabilities}</div>
                <div className="card-change">Debt obligations</div>
              </div>

              <div 
                className={`summary-card equity ${selectedMetric === 'equity' ? 'selected' : ''}`}
                onClick={() => handleMetricClick('equity')}
              >
                <div className="card-header">
                  <span className="card-icon">👑</span>
                  <h3>Net Worth</h3>
                </div>
                <div className="card-value">{summary.totalEquity}</div>
                <div className="card-change positive">↗ Your equity</div>
              </div>

              <div 
                className={`summary-card income ${selectedMetric === 'income' ? 'selected' : ''}`}
                onClick={() => handleMetricClick('income')}
              >
                <div className="card-header">
                  <span className="card-icon">💰</span>
                  <h3>Monthly Income</h3>
                </div>
                <div className="card-value">{summary.monthlyIncome}</div>
                <div className="card-change positive">Revenue streams</div>
              </div>

              <div 
                className={`summary-card net-income ${selectedMetric === 'netIncome' ? 'selected' : ''}`}
                onClick={() => handleMetricClick('netIncome')}
              >
                <div className="card-header">
                  <span className="card-icon">📈</span>
                  <h3>Net Income</h3>
                </div>
                <div className={`card-value ${summary.rawRatios.netIncome >= 0 ? 'positive' : 'negative'}`}>
                  {summary.netIncome}
                </div>
                <div className={`card-change ${summary.rawRatios.netIncome >= 0 ? 'positive' : 'negative'}`}>
                  {summary.rawRatios.netIncome >= 0 ? '↗ Profitable' : '↘ Loss'}
                </div>
              </div>
            </div>

            {/* Three Column Layout */}
            <div className="dashboard-three-columns">
              {/* Left Column - Quick Actions */}
              <div className="left-column">
                <div className="quick-actions">
                  <h3 className="section-title">Quick Actions</h3>
                  <div className="actions-list">
                    <button className="action-button" onClick={() => setActiveView('balance-sheet')}>
                      <span className="action-icon">🏦</span>
                      <span className="action-text">View Balance Sheet</span>
                    </button>
                    <button className="action-button" onClick={() => setActiveView('profit-loss')}>
                      <span className="action-icon">💰</span>
                      <span className="action-text">View P&L Statement</span>
                    </button>
                    <button className="action-button">
                      <span className="action-icon">📊</span>
                      <span className="action-text">Add Transaction</span>
                    </button>
                    <button className="action-button">
                      <span className="action-icon">🔄</span>
                      <span className="action-text">Connect Bank</span>
                    </button>
                  <button className="action-button" onClick={() => navigate('/finance/portfolio')}>
                      <span className="action-icon">📈</span>
                      <span className="action-text">Investment Portfolio</span>
                    </button>
                    <button className="action-button">
                      <span className="action-icon">🎯</span>
                      <span className="action-text">Set Financial Goals</span>
                    </button>
                  </div>
                </div>
              </div>

              {/* Middle Column - Interactive Chart */}
              <div className="middle-column">
                <FinancialChart 
                  selectedMetric={selectedMetric}
                  data={summary}
                />
              </div>

              {/* Right Column - Financial Ratios */}
              <div className="right-column">
                <div className="ratios-section">
                  <h3 className="section-title">Key Financial Ratios</h3>
                  <div className="ratios-list">
                    <div className="ratio-card">
                      <div className="ratio-label">Current Ratio</div>
                      <div className="ratio-value">{summary.currentRatio}</div>
                      <div className="ratio-description">Liquidity measure</div>
                    </div>
                    <div className="ratio-card">
                      <div className="ratio-label">Return on Assets</div>
                      <div className="ratio-value">{summary.returnOnAssets}</div>
                      <div className="ratio-description">Asset efficiency</div>
                    </div>
                    <div className="ratio-card">
                      <div className="ratio-label">Debt to Assets</div>
                      <div className="ratio-value">
                        {summary.rawRatios.totalAssets > 0 ? 
                          ((summary.rawRatios.totalLiabilities / summary.rawRatios.totalAssets) * 100).toFixed(1) + '%' : 
                          '0%'
                        }
                      </div>
                      <div className="ratio-description">Leverage measure</div>
                    </div>
                    <div className="ratio-card">
                      <div className="ratio-label">Net Profit Margin</div>
                      <div className="ratio-value">
                        {summary.rawRatios.monthlyIncome > 0 ? 
                          ((summary.rawRatios.netIncome / summary.rawRatios.monthlyIncome) * 100).toFixed(1) + '%' : 
                          '0%'
                        }
                      </div>
                      <div className="ratio-description">Profitability</div>
                    </div>
                    <div className="ratio-card">
                      <div className="ratio-label">Savings Rate</div>
                      <div className="ratio-value">
                        {summary.rawRatios.monthlyIncome > 0 ? 
                          ((summary.rawRatios.netIncome / summary.rawRatios.monthlyIncome) * 100).toFixed(1) + '%' : 
                          '0%'
                        }
                      </div>
                      <div className="ratio-description">Financial discipline</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeView === 'balance-sheet' && (
          <BalanceSheet 
            financialData={financialData}
            entityName="Personal Finances"
            currency="USD"
            asOfDate={new Date()}
          />
        )}

        {activeView === 'profit-loss' && (
          <ProfitLoss 
            financialData={financialData}
            entityName="Personal Finances"
            currency="USD"
            periodStart={new Date(new Date().getFullYear(), new Date().getMonth(), 1)}
            periodEnd={new Date()}
          />
        )}
      </div>

      {/* Data Source Notice */}
      <div className="data-notice">
        <p>
          📊 <strong>Demo Data:</strong> This dashboard shows mock financial data for demonstration. 
          In Simple mode, connect your bank accounts or manually enter transactions to see real data.
        </p>
      </div>
    </div>
  );
}

export default PersonalDashboard; 